import React, { useState, useEffect } from "react";
import { FaArrowRight, FaLightbulb, FaCode, FaRocket, FaHeadset, FaRobot, FaCloud, FaShieldAlt, FaSync, FaChartLine, FaDatabase, FaMicrochip } from "react-icons/fa";
import { Container, Row, Col } from 'react-bootstrap';
import "../help.css"

const TechHelp = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [progress, setProgress] = useState(0);

    const techIcons = [
        <FaMicrochip className="tech-icon" />,
        <FaCode className="tech-icon" />,
        <FaRocket className="tech-icon" />,
        <FaHeadset className="tech-icon" />
    ];

    const techColors = ['#ff7e5f', '#61DAFB', '#4CAF50', '#9C27B0'];
    const techGradients = [
        'linear-gradient(135deg, #ff7e5f, #ff512f)',
        'linear-gradient(135deg, #61DAFB, #21a1f1)',
        'linear-gradient(135deg, #4CAF50, #2E7D32)',
        'linear-gradient(135deg, #9C27B0, #6A1B9A)'
    ];

    const steps = [
        {
            title: "ANALYSE TECH INTELLIGENTE",
            subtitle: "IA & Data Mining",
            description: "Utilisation de l'IA pour analyser les besoins et identifier les opportunités d'optimisation avec précision algorithmique.",
            tech: ["AI Analytics", "Data Mining", "ML Models", "Pattern Recognition"],
            icon: <FaMicrochip />,
            stats: { accuracy: "99.8%", speed: "2.4s", data: "10TB+" }
        },
        {
            title: "ARCHITECTURE DIGITALE",
            subtitle: "Cloud & Microservices",
            description: "Conception d'architectures scalables basées sur le cloud et les microservices pour une performance optimale.",
            tech: ["AWS/Azure", "Docker", "Kubernetes", "API Gateway"],
            icon: <FaCloud />,
            stats: { scalability: "∞", latency: "<50ms", uptime: "99.99%" }
        },
        {
            title: "DÉPLOIEMENT QUANTUM",
            subtitle: "DevOps & CI/CD",
            description: "Déploiement automatisé avec pipelines CI/CD avancés pour une livraison continue et sans interruption.",
            tech: ["Jenkins", "GitLab CI", "Terraform", "Ansible"],
            icon: <FaRocket />,
            stats: { deployment: "Auto", frequency: "100+/day", recovery: "<5min" }
        },
        {
            title: "OPTIMISATION INTELLIGENTE",
            subtitle: "AI Monitoring & Support",
            description: "Surveillance proactive par IA et support 24/7 pour maintenir les performances au niveau maximum.",
            tech: ["AI Ops", "Real-time", "Predictive", "Auto-healing"],
            icon: <FaRobot />,
            stats: { response: "<30s", resolution: "95%", satisfaction: "100%" }
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveStep(prev => (prev + 1) % steps.length);
        }, 5000);

        // Animation de progression
        const progressInterval = setInterval(() => {
            setProgress(prev => prev >= 100 ? 0 : prev + 1);
        }, 50);

        return () => {
            clearInterval(interval);
            clearInterval(progressInterval);
        };
    }, []);

    return (
        <div className="tech-help-section">
            {/* Effets de fond tech */}
            <div className="tech-background-effects">
                <div className="circuit-board"></div>
                <div className="data-streams">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="data-stream" style={{ '--delay': `${i * 0.5}s` }}></div>
                    ))}
                </div>
                <div className="floating-particles">
                    {[...Array(12)].map((_, i) => (
                        <div key={i} className="floating-particle"></div>
                    ))}
                </div>
            </div>

            <Container>
                {/* Header futuriste */}
                <div className="tech-process-header">
                    <div className="tech-title-container">
                        <div className="tech-title-glow">PROCESSUS TECH</div>
                        <h2 className="tech-main-title">
                            <span className="tech-title-part">ALGORITHME</span>
                            <span className="tech-title-accent">D'INNOVATION</span>
                            <span className="tech-title-part">DIGITALE</span>
                        </h2>
                        <div className="tech-subtitle">
                            <span className="tech-subtitle-text">SYSTÈME DE TRANSFORMATION TECH</span>
                            <div className="tech-subtitle-icons">
                                <FaMicrochip />
                                <FaCode />
                                <FaCloud />
                                <FaRobot />
                            </div>
                        </div>
                    </div>

                    {/* Barre de progression tech */}
                    <div className="tech-progress-track">
                        <div className="progress-line">
                            <div
                                className="progress-fill"
                                style={{
                                    width: `${progress}%`,
                                    background: techGradients[activeStep]
                                }}
                            ></div>
                        </div>
                        <div className="progress-markers">
                            {steps.map((_, index) => (
                                <div
                                    key={index}
                                    className={`progress-marker ${index === activeStep ? 'active' : ''}`}
                                    style={{ '--marker-color': techColors[index] }}
                                    onClick={() => setActiveStep(index)}
                                >
                                    <div className="marker-glow"></div>
                                    <div className="marker-pulse"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Carte étape active (version holographique) */}
                <div className="active-tech-step">
                    <div
                        className="hologram-card"
                        style={{ '--hologram-color': techColors[activeStep] }}
                    >
                        {/* Effet holographique */}
                        <div className="hologram-effect"></div>

                        {/* Contenu */}
                        <div className="hologram-content">
                            <div className="step-header">
                                <div className="step-icon-wrapper">
                                    <div
                                        className="step-icon-bg"
                                        style={{ background: techGradients[activeStep] }}
                                    >
                                        {techIcons[activeStep]}
                                    </div>
                                    <div className="step-icon-ring"></div>
                                </div>

                                <div className="step-title-section">
                                    <div className="step-number">0{activeStep + 1}</div>
                                    <h3 className="step-title">{steps[activeStep].title}</h3>
                                    <div className="step-subtitle">{steps[activeStep].subtitle}</div>
                                </div>
                            </div>

                            <p className="step-description">{steps[activeStep].description}</p>

                            {/* Stats en temps réel */}
                            <div className="tech-step-stats">
                                {Object.entries(steps[activeStep].stats).map(([key, value], idx) => (
                                    <div key={key} className="tech-stat">
                                        <div className="stat-value">{value}</div>
                                        <div className="stat-label">{key.toUpperCase()}</div>
                                        <div className="stat-progress">
                                            <div
                                                className="stat-progress-fill"
                                                style={{
                                                    width: `${(idx + 1) * 25}%`,
                                                    background: techColors[activeStep]
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Stack technologique */}
                            <div className="tech-stack-container">
                                {steps[activeStep].tech.map((tech, idx) => (
                                    <div
                                        key={tech}
                                        className="tech-stack-item"
                                        style={{ '--item-delay': `${idx * 0.2}s` }}
                                    >
                                        <span className="tech-stack-text">{tech}</span>
                                        <div className="tech-stack-glow"></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Effets de bordure */}
                        <div className="hologram-border"></div>
                    </div>
                </div>

                {/* Grille des étapes (miniatures) */}
                <Row className="tech-steps-grid">
                    {steps.map((step, index) => (
                        <Col key={index} lg={3} md={6} className="tech-step-col">
                            <div
                                className={`tech-step-mini ${index === activeStep ? 'active' : ''}`}
                                onClick={() => setActiveStep(index)}
                                style={{ '--step-color': techColors[index] }}
                            >
                                <div className="mini-step-header">
                                    <div className="mini-step-icon">{step.icon}</div>
                                    <div className="mini-step-number">0{index + 1}</div>
                                </div>

                                <h4 className="mini-step-title">{step.title.split(' ')[0]}</h4>
                                <div className="mini-step-line"></div>

                                <div className="mini-step-tech">
                                    {step.tech.slice(0, 2).map(tech => (
                                        <span key={tech} className="mini-tech-tag">{tech}</span>
                                    ))}
                                </div>

                                {/* Indicateur d'activation */}
                                <div className="mini-step-indicator">
                                    <div className="indicator-dot"></div>
                                    <div className="indicator-pulse"></div>
                                </div>

                                {/* Flèche de connexion */}
                                {index < steps.length - 1 && (
                                    <div className="step-connector">
                                        <FaArrowRight className="connector-arrow" />
                                        <div className="connector-line"></div>
                                    </div>
                                )}
                            </div>
                        </Col>
                    ))}
                </Row>

                {/* Indicateur de phase */}
                <div className="phase-indicator">
                    <div className="phase-label">PHASE ACTIVE</div>
                    <div className="phase-dots">
                        {steps.map((_, idx) => (
                            <div
                                key={idx}
                                className={`phase-dot ${idx === activeStep ? 'active' : ''}`}
                                style={{ '--dot-color': techColors[idx] }}
                            ></div>
                        ))}
                    </div>
                    <div className="phase-text">
                        <span className="phase-name">{steps[activeStep].subtitle}</span>
                        <span className="phase-status">• EN COURS</span>
                    </div>
                </div>

                {/* Console de debug (effet tech) */}
                <div className="tech-debug-console">
                    <div className="console-header">
                        <div className="console-dots">
                            <div className="console-dot red"></div>
                            <div className="console-dot yellow"></div>
                            <div className="console-dot green"></div>
                        </div>
                        <div className="console-title">SYSTEM PROCESSOR</div>
                    </div>
                    <div className="console-content">
                        <div className="console-line">
                            <span className="console-prompt">$</span>
                            <span className="console-text">
                                Processing phase {activeStep + 1}: {steps[activeStep].title.toLowerCase()}
                            </span>
                            <span className="console-cursor">_</span>
                        </div>
                        <div className="console-line">
                            <span className="console-prompt">></span>
                            <span className="console-text">
                                Tech stack: {steps[activeStep].tech.join(' • ')}
                            </span>
                        </div>
                        <div className="console-line">
                            <span className="console-prompt">#</span>
                            <span className="console-text">
                                Status: <span className="console-status active">ACTIVE</span>
                            </span>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default TechHelp;